import type { ProgrammeVersion } from "@/types";

// The lookbehind stops "1.5 years" reading as 5.
const YEAR_COUNT = /(?<![\d.])(\d+(?:\.\d+)?)\s*(?:academic\s+)?years?\b/gi;

/**
 * The longest year count a free-text duration names, as "1 year FT / 2 years
 * PT" covers two years. Undefined when the text names no years.
 */
export function longestDurationYears(duration: string | undefined): number | undefined {
  const counts = [...(duration ?? "").matchAll(YEAR_COUNT)].map((match) => Number(match[1]));
  return counts.length > 0 ? Math.max(...counts) : undefined;
}

/** The highest programme year a version's stages reach, or 0 when none is set. */
export function stageYears(version: ProgrammeVersion): number {
  return Math.max(0, ...(version.stages ?? []).map((stage) => Number(stage.academicStage ?? 0)));
}

/**
 * A version's duration in years: the advertised duration, which a part-time
 * version runs past its stage years, else the years its stages reach.
 */
export function versionDurationYears(version: ProgrammeVersion): number | undefined {
  const fromStages = stageYears(version);
  return longestDurationYears(version.duration) ?? (fromStages > 0 ? fromStages : undefined);
}
